import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { BulkOpeningHoursBody, OpeningHours } from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '@/lib/analytics/umami-client';

const OPENING_HOURS_PATH = '/api/restaurants/{restaurantId}/opening-hours';
const OPENING_HOURS_SERVICE_PATH =
  '/api/restaurants/{restaurantId}/opening-hours/service/{service}';

export function useOpeningHours() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  return useQuery({
    queryKey: ['opening-hours', restaurantId],
    queryFn: async () => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<OpeningHours[]>(
        await openapiClient.GET(OPENING_HOURS_PATH, { params: { path: { restaurantId } } }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useSetOpeningHours() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BulkOpeningHoursBody) => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<OpeningHours[]>(
        await openapiClient.PUT(OPENING_HOURS_PATH, { params: { path: { restaurantId } }, body }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opening-hours', restaurantId] });
      trackEvent('setup_changed', { area: 'hours' });
    },
  });
}

/**
 * Drop a channel's custom weekly rows so it follows the base hours again.
 * BASE is rejected server-side - only PICKUP / DELIVERY / RESERVATION are valid.
 */
export function useClearOpeningHours() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (service: OpeningHours['service']) => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<OpeningHours[]>(
        await openapiClient.DELETE(OPENING_HOURS_SERVICE_PATH, {
          params: { path: { restaurantId, service } },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opening-hours', restaurantId] });
      trackEvent('setup_changed', { area: 'hours' });
    },
  });
}
